import { useMemo, useState } from 'react';
import { CalendarGrid } from '@/components/calendar/CalendarGrid';
import { DayModal } from '@/components/calendar/DayModal';
import { MonthPicker } from '@/components/calendar/MonthPicker';
import { ScheduleShiftDialog } from '@/components/calendar/ScheduleShiftDialog';
import { Button } from '@/components/ui/button';
import { useActiveCnpjId, useServices } from '@/features/services/hooks';
import { useCalendarMonth } from '@/features/shifts/hooks';

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function CalendarPage() {
  const [month, setMonth] = useState<string>(currentMonth());
  const [serviceFilter, setServiceFilter] = useState<string>('');
  const [openDay, setOpenDay] = useState<string | null>(null);
  const [scheduleDate, setScheduleDate] = useState<string | null>(null);

  const { cnpjId } = useActiveCnpjId();
  const { data: services = [] } = useServices(cnpjId);
  const shiftsQuery = useCalendarMonth(cnpjId, month, serviceFilter || null);
  const shifts = useMemo(() => shiftsQuery.data ?? [], [shiftsQuery.data]);

  const handleDayClick = (dateISO: string): void => {
    setOpenDay(dateISO);
  };

  const handleSchedule = (dateISO: string): void => {
    setOpenDay(null);
    setScheduleDate(dateISO);
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">Calendário</h1>
          <p className="text-sm text-(--color-fg-muted)">
            Plantões do mês por serviço.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <MonthPicker month={month} onChange={setMonth} />
          <select
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
            className="h-9 rounded-md border border-(--color-border) bg-(--color-surface) px-2 text-sm"
            aria-label="Filtrar por serviço"
          >
            <option value="">Todos os serviços</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <Button onClick={() => setScheduleDate(new Date().toISOString().slice(0, 10))}>
            Novo plantão
          </Button>
        </div>
      </header>

      {shiftsQuery.isLoading ? (
        <p className="text-sm text-(--color-fg-muted)">Carregando…</p>
      ) : (
        <CalendarGrid
          month={month}
          shifts={shifts}
          services={services}
          onDayClick={handleDayClick}
        />
      )}

      <DayModal
        open={!!openDay}
        onOpenChange={(o) => !o && setOpenDay(null)}
        dateISO={openDay}
        shifts={shifts}
        services={services}
        cnpjId={cnpjId}
        onSchedule={handleSchedule}
      />

      <ScheduleShiftDialog
        open={!!scheduleDate}
        onOpenChange={(o) => !o && setScheduleDate(null)}
        dateISO={scheduleDate}
        services={services}
        cnpjId={cnpjId}
      />
    </div>
  );
}
